import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import slice from 'lodash/slice';
import uniq from 'lodash/uniq';
import max from 'lodash/max';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import flatten from 'lodash/flatten';
import pick from 'lodash/pick';
import differenceBy from 'lodash/differenceBy';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';
import mean from 'lodash/mean';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';

import {
  SIZE_NODES_BY_OPTIONS,
  VIEW_BY_OPTIONS,
  AUTOMATION_COLORS,
  AUTOMATION_COLOR_UNKNOWN,
  CLUSTER_AUTHOR_VALUES
} from './constants';
const VIEW_BY_OPTION_VALUES = map(VIEW_BY_OPTIONS.all, 'value');
const SIZE_BY_OPTION_VALUES = map(SIZE_NODES_BY_OPTIONS, 'value');
const CLUSTER_LIMIT = 100;
function _createMapOfEmptyArrays(keys) {
  return keys.reduce((aggregate, key) => {
    aggregate[key] = [];
    return aggregate;
  }, {});
}

function _createMapOfEmptyObjects(keys) {
  return keys.reduce((aggregate, key) => {
    aggregate[key] = {};
    return aggregate;
  }, {});
}

function _createInitializedMap(keys, initialValue) {
  return keys.reduce((aggregate, key) => {
    aggregate[key] = initialValue;
    return aggregate;
  }, {});
}

function _createSizeMaps(authorsById) {
  const maxSizeMap = _createInitializedMap(SIZE_BY_OPTION_VALUES, Number.NEGATIVE_INFINITY);
  const minSizeMap = _createInitializedMap(SIZE_BY_OPTION_VALUES, Number.POSITIVE_INFINITY);

  forEach(authorsById, ({author}) => {
    forEach(SIZE_BY_OPTION_VALUES, (sizeBy) => {
      const rawSize = author[sizeBy];
      if(rawSize !== undefined && rawSize >= 0) {
        // we want to ignore raw data values for influence, that are larger than 100
        if (!(sizeBy === 'influenceScore' &&  rawSize > 100)) {
          maxSizeMap[sizeBy] = Math.max(maxSizeMap[sizeBy], rawSize);
          minSizeMap[sizeBy] = Math.min(minSizeMap[sizeBy], rawSize);
        }
      }
    });
  });

  return { maxSizeMap, minSizeMap };
}


function _normalizeSize(rawSize, mx, min) {

  const MAX_CIRCLE_SIZE = 46;
  const MIN_CIRCLE_SIZE = 16;

  // Corner cases: incomplete author data or unlikely homogeneous data or wrong (negative) data
  if(rawSize === undefined || min === mx || rawSize <  0) {
    return MIN_CIRCLE_SIZE;
  }

  const size = (Math.cbrt(rawSize) -  Math.cbrt(min)  ) / ( Math.cbrt(mx) -  Math.cbrt(min)  )
    *  (MAX_CIRCLE_SIZE - MIN_CIRCLE_SIZE) + MIN_CIRCLE_SIZE;
  return Math.floor(size);
}

function _getSizesForAuthor(author, maxSizeMap, minSizeMap) {
  return SIZE_BY_OPTION_VALUES.reduce((normalizedSizeMap, sizeBy) => {
    normalizedSizeMap[sizeBy] = _normalizeSize(author[sizeBy], maxSizeMap[sizeBy], minSizeMap[sizeBy]);
    return normalizedSizeMap;
  }, {});
}

function _getMaxSize(sizes) {
  return max(values(sizes));
}

function _createLinksForClusterNode(clusterNode) {
  return map(clusterNode.authorIds, (authorId) => {
    return {
      id: `${authorId}|${clusterNode.id}`,
      linkType: 'cluster',
      strength: 0.2,
      source: authorId,
      target: clusterNode.id,
      color: 'grey',
      curvature: 0.1
    };
  });
}

function _createHoverMapForClusterNodes(clusters, authorNodesById) {
  return clusters.reduce((clusterResults, cluster) => {
    const { authorIds, id, nodeType, clusterType } = cluster;
    const viewByOption = VIEW_BY_OPTIONS.all.find(({value}) => clusterType === value);
    const hoverMapDefaults = get(viewByOption, 'hoverMapDefaults', {});
    const hoverMap = Object.assign({
      hoverId: id,
      categoryId: id,
      nodeType,
      clusterIds: { [id]: true },
      linkIds: {}
    }, hoverMapDefaults);

    clusterResults[id] = authorIds.reduce((result, authorId) => {
      const authorNode = authorNodesById[authorId];
      const clusterIds = Object.keys(authorNode.targetClusters);
      const clusterIdsFiltered = filter(clusterIds, (clusterId) => clusterId.indexOf(clusterType) === 0);
      forEach(clusterIdsFiltered, (clusterId) => {
        if (clusterId !== id) {
          result.clusterIds[clusterId] = true;
          result.linkIds[`${authorId}|${clusterId}`] = true;
        }
      });
      return result;
    }, hoverMap);

    return clusterResults;
  }, {});
}

function _getAutomationColor(score) {
  if (!isNumber(score) || score < 0 || score > 100){
    return AUTOMATION_COLOR_UNKNOWN;
  }
  let index = 0;
  if (score <= 20){
    index = 0;
  } else if (score <= 40){
    index = 1;
  } else if (score <= 60){
    index = 2;
  } else if (score <= 80){
    index = 3;
  } else if (score <= 100){
    index = 4;
  }
  return AUTOMATION_COLORS[index];
}

function _createAuthorNode(data, maxSizeMap, minSizeMap){
  const { author } = data;
  const { id, automationScore } = author;
  const normalizedSizes = _getSizesForAuthor(author, maxSizeMap, minSizeMap);
  return {
    data, // for tooltip
    id,
    normalizedSizes,
    targetClusters: {},
    gravity: -600,
    maxSize: _getMaxSize(normalizedSizes),
    nodeType: 'author',
    size: 16,
    color: _getAutomationColor(automationScore)
  };
}

function _createClusterNode(id, label, clusterType, clusterLabelsLength){
  return {
    id,
    label,
    clusterType,
    authorIds: [],
    gravity: Math.max(clusterLabelsLength * -80, -2000),
    nodeType: 'cluster',
    size: 16
  };
}

function _createClusterNodes(data, authorNode, clusterNodesById, clusterNodesByType){
  const { author, stories } = data;
  const { id: authorId } = author;
  // Get "clusters" (entities, titles, domains, etc.) from all stories from author
  forEach(clusterNodesByType, (clusterNodes, clusterType) => {
    // depending on clusterType, we check the author or the stories
    const clusterData = CLUSTER_AUTHOR_VALUES.includes(clusterType) ? [author] : stories;
    const clusterLabels = uniq(compact(flatMap(clusterData, clusterType)));

    // Create/add clusterNodes
    const newClusterNodes = [];
    clusterLabels.forEach((clusterLabel) => {
      // handle potential numbers
      const clusterLabelStr = `${clusterLabel}`;
      // create unique id
      const clusterId = `${clusterType}|${clusterLabelStr}`;
      // need to keep track of target clusters per author for graph hovering
      authorNode.targetClusters[clusterId] = true;

      const clusterNode = clusterNodesById[clusterId]
        || _createClusterNode(clusterId, clusterLabelStr, clusterType, clusterLabels.length);
      // add new clusterNodes
      if (!clusterNodesById[clusterId]){
        newClusterNodes.push(clusterNode);
        clusterNodesById[clusterId] = clusterNode;
      }
      clusterNode.authorIds.push(authorId);
    });

    clusterNodesByType[clusterType] = [...clusterNodes, ...newClusterNodes];
  });
}

function _createLinksAndHoverMaps(authorNodesById, viewBy, graphData){
  const { clusterNodesByType, clusterLinksByType, clusterHoverMapsByType } = graphData;
  forEach(clusterNodesByType, (clusterNodes, clusterType) => {
    clusterNodes.sort((a, b) => {
      /* Sort by author count, then bot count, then label (stable tie-breaker)
       * Tertiary label comparison ensures deterministic order across JS engines */
      if (b.authorIds.length !== a.authorIds.length) {
        return b.authorIds.length - a.authorIds.length;
      }
      if (b.botCount !== a.botCount) {
        return b.botCount - a.botCount;
      }
      return a.label.localeCompare(b.label);
    });
    // Get top clusters by volume
    const categoryLimit = 20;
    const limit = (viewBy === 'cluster') ? CLUSTER_LIMIT : categoryLimit;
    let topClusterNodes = slice(clusterNodes, 0, limit);
    if (viewBy === 'cluster') {
      topClusterNodes = topClusterNodes.filter((clusterNode) => clusterNode.authorIds.length > 1);
    }
    clusterNodesByType[clusterType] = topClusterNodes;
    clusterLinksByType[clusterType] = flatMap(topClusterNodes, _createLinksForClusterNode);
    clusterHoverMapsByType[clusterType] = _createHoverMapForClusterNodes(topClusterNodes, authorNodesById);
  });

  const topPicks = map(flatten(values(clusterNodesByType)), 'id');
  forEach(authorNodesById, (node) => {
    node.targetClusters = pick(node.targetClusters, topPicks);
  });

}
function _getAvgAuthorAutmationScore({authorNodesById, authorIds}){

  const authorScores = authorIds
    .map((id) => get(authorNodesById[id], 'data.author.automationScore'))
    .filter((score) => isNumber(score) && !isNaN(score));

  return authorScores.length ? round(mean(authorScores)) : 'Unknown';
}

/**
 * Derive/transform data from server into format suitable for <ClusterGraph>
 *
 * @param {Object} authorsById - response body from API call with authors & stories indexed by authorId
 * @param {String} viewBy - View by either cluster or category.
 * This param will change the limit to 10 clusters or more for category.
 * @returns {Object}
 *  {
 *    authorNodes,
 *    clusterLinksByType,
 *    clusterNodesByType,
 *  }
 */
function createClusterGraphData(authorsById, viewBy) {
  const authorNodesById = {}; // used to quickly look up authors for volume
  const clusterNodesById = {}; // used to prevent duplicate clusterNodes
  const clusterLinksByType = _createMapOfEmptyArrays(VIEW_BY_OPTION_VALUES);
  const clusterNodesByType = _createMapOfEmptyArrays(VIEW_BY_OPTION_VALUES);
  const clusterHoverMapsByType = _createMapOfEmptyObjects(VIEW_BY_OPTION_VALUES);

  const graphData = {
    authorNodes: [],
    clusterLinksByType,
    clusterNodesByType,
    clusterHoverMapsByType
  };
  // early exit
  if(authorsById === undefined || Object.keys(authorsById).length < 1) {
    return graphData;
  }

  const {
    maxSizeMap,
    minSizeMap
  } = _createSizeMaps(authorsById);

  /* Process authors in deterministic order to ensure consistent cluster building
   * Object iteration order varies across JS engines for numeric string keys */
  const sortedAuthorIds = Object.keys(authorsById).sort();
  sortedAuthorIds.forEach((authorId) => {
    const data = authorsById[authorId];
    const authorNode = _createAuthorNode(data, maxSizeMap, minSizeMap);
    authorNodesById[authorId] = authorNode;
    _createClusterNodes(data, authorNode, clusterNodesById, clusterNodesByType);
  });

  _createLinksAndHoverMaps(authorNodesById, viewBy, graphData);

  //get the average automation score for each object in each type
  const clusterTypes = Object.keys(graphData.clusterNodesByType);

  forEach(clusterTypes, (type) => {
    const curCluster = graphData.clusterNodesByType[type];
    curCluster.forEach((cluster) => {
      cluster.avgAutomationScore = _getAvgAuthorAutmationScore({authorNodesById, authorIds: cluster.authorIds});
    });
  });

  return Object.assign(graphData, { authorNodes: values(authorNodesById) });
}


/**
 * Helper filter functions for author and cluster nodes
 * used by 'filterNodes'
 */

function _filterSelectedClusterNodes(clusterNodes, selectedClusters) {
  return selectedClusters.length
    ? filter(clusterNodes, ({id}) => selectedClusters.includes(id))
    : clusterNodes;
}

function _filterAuthorNodesBySelectedClusters(authorNodes, clusterNodes, selectedClusters) {
  if (!selectedClusters.length) {
    return authorNodes;
  }
  const linkedIds = uniq(clusterNodes.reduce((results, {authorIds}) => [...results, ...authorIds], []));
  // only show authors linked to cluster nodes
  return filter(authorNodes, ({id}) => linkedIds.includes(id));
}

function _filterAuthorNodesByScore(authorNodes, {min: minScore, max: maxScore, isUnknownIncluded}) {
  return filter(authorNodes, ({data: {author: { automationScore }}}) => {
    const isValueBetweenBoundaries = isNumber(automationScore)
      && minScore <= automationScore && automationScore <= maxScore;

    return isUnknownIncluded
      ? automationScore === null || isValueBetweenBoundaries
      : isValueBetweenBoundaries;
  });
}

/**
 * Filter nodes based on the automation score and the clusters selected.
 * @param authorNodes List of nodes within the graph.
 * @param clusterNodes List of clusters.
 * @param filteredAutomationScore Automation Score to filter. eg. with a score between 50 and 100, including Unknown.
 * @param selectedClusters Array of cluster nodes selected to filter. Default is empty array.
 * @returns {Array} Set of author and cluster nodes filtered.
 */
function filterNodes({
  authorNodes,
  clusterNodes,
  filteredAutomationScore,
  selectedClusters
}) {
  // cluster node filtering
  const clusters = _filterSelectedClusterNodes(clusterNodes, selectedClusters);
  // author node filtering
  let authors = _filterAuthorNodesBySelectedClusters(authorNodes, clusters, selectedClusters);
  authors = _filterAuthorNodesByScore(authors, filteredAutomationScore);
  // return all nodes concatenated
  return [ ...authors, ...clusters ];
}

/**
 * Same as `filterNodes` but without cluster nodes.
 * @param options Same object argument as well.
 * @returns {Array} Set of author nodes filtered.
 */
function filterAuthorNodesOnly(options) {
  const authorAndClusterNodes = filterNodes(options);
  return filter(authorAndClusterNodes, ({nodeType}) => nodeType !== 'cluster');
}

/**
 * After the legend is used to hide nodes, we need to filter out links that are connecting those hidden nodes.
 * @param links Cluster links.
 * @param nodes List of nodes within the graph.
 * @returns {Array} Cluster links filtered.
 */
function filterLinks(links, nodes) {
  if (!links.length || !nodes.length) {
    return [];
  }
  const nodesById = nodes.reduce((mapping, node) => {
    mapping[node.id] = node;
    return mapping;
  }, {});
  return links.reduce((result, link) => {
    const { source, target } = link;
    // Type-checking is necessary because force-graph library mutates links
    // so then their `source` and `target` begin as numbers/strings, but are
    // replaced by actual nodes (POJOs).
    const sourceId = isPlainObject(source) ? source.id : source;
    const targetId = isPlainObject(target) ? target.id : target;
    const sourceNode = nodesById[sourceId];
    const targetNode = nodesById[targetId];
    if(sourceNode !== undefined && targetNode !== undefined) {
      // ENG-13888 - Replace old node references
      link.source = sourceNode;
      link.target = targetNode;
      result.push(link);
    }
    return result;
  }, []);
}

/**
 * Format twitter username to get it without the `@` prefix character.
 * @param username With or without the prefix.
 * @returns {String} Username without the prefix.
 */
function formatTwitterUsername(username) {
  if (!username) { return ''; }
  return username[0] === '@' ? username.slice(1) : username;
}

function compareClusterNodes(clusterNodesA, clusterNodesB) {
  if (clusterNodesA.length !== clusterNodesB.length) {
    return true;
  }

  return !isEmpty(differenceBy(clusterNodesA, clusterNodesB, 'id'));
}

/**
 * Generate options for  additional Filter's Select control based on cluterNodes
 * with and without group
 */

function getAdditionalFilterOptions(clusterNodes, groupMapping) {

  if(!groupMapping || (groupMapping && !groupMapping.data))  {
    return map(clusterNodes, (node) => {
      const {label, id: value} = node;
      return ({
        label,
        value: `nogroup|${value}`
      });
    });
  }
  const { data, column } = groupMapping;

  return data.reduce((acc, currGroup)=>{
    const getValue = (issue) => clusterNodes && clusterNodes.find((node) => issue.includes(node.id.split('|')[1]));
    const groupOption = {
      label: currGroup.name,
      options: currGroup[column].map((issue) =>({
        label: issue,
        value: `${currGroup.name}|${getValue(issue) ? getValue(issue).id: issue}`,
        isDisabled: getValue(issue) === undefined
      })
      )};
    return acc.concat(groupOption);
  },[]);
}

/**
 * Helper functions for csv export formatting
 */

function getAuthorsAndStoriesToExport(authorNodes) {
  const keys = [
    'username',
    'state',
    'storyTitles',
    'body',
    'countryCode',
    'location',
    'followerCount',
    // 'retweetCount', - ENG-15172 - Plan for now – remove the field that is always showing zero.
    'storyDescriptions',
    'domain',
    'friendCount',
    'name',
    'originalCreatedAt', // Not present in keyword search
    'createdAt',
    'storyUrls',
    'automationScore',
    'influenceScore',
    'id',
    'rawLinks', // Not present in keyword search
    'domainReversed', // Not present in keyword search
    'originalTitle',
    'authorApplication',
    'type',
    'postCount',
    'rawPersistedAt', // Not present in keyword search
    'verified',
    'bio',
    'sentiment' // Present as Unknown, Positive, Negative
  ];

  const emptyObject = keys.reduce((obj, key) => ({ ...obj, [key]: ''}), {});

  return flatMap(authorNodes, ({data: {author, stories}}, index) => {
    return map(stories, (story) => {
      const emptyHeadersIfFirstStory = index === 0 ? emptyObject : {};
      const storyAndAuthor = Object.assign({}, emptyHeadersIfFirstStory, story, author);
      return pick(storyAndAuthor, keys);
    });
  });
}

export {
  createClusterGraphData,
  filterNodes,
  filterAuthorNodesOnly,
  filterLinks,
  formatTwitterUsername,
  compareClusterNodes,
  getAdditionalFilterOptions,
  getAuthorsAndStoriesToExport,
};
